import { type App, type Ref, ref, watch } from 'vue'
import { inject } from 'vue'
import { type ReactiveCookiePlugin } from '@/plugins/cookie-plugin.ts'
import { getWindow } from '@/helpers/globals.ts'
import { useScriptTag } from '@vueuse/core'
import { type AuthPlugin } from '@/plugins/auth.ts'

const key = 'plugin.intercom'

export interface IntercomPlugin {
    appId: string | undefined

    isLoaded: Ref<boolean>
    unreadCount: Ref<number>
    chatStarted: Ref<boolean>

    load(): Promise<any>
    unload(): void
}

export interface Intercom {
    (...args: any[]): void
    q: any[]
    c(...args: any[]): void
}

export function installIntercom(
    app: App,
    cookiePlugin: ReactiveCookiePlugin,
    auth: AuthPlugin,
    appId: string | undefined
): IntercomPlugin {
    const plugin: IntercomPlugin = createIntercom(cookiePlugin, appId)

    const window = getWindow()

    function Intercom(...args: any[]) {
        Intercom.c(args)
    }
    Intercom.q = [] as any[]
    Intercom.c = function (args: any) {
        Intercom.q.push(args)
    }

    if (window) {
        window.Intercom = Intercom
    }

    const { user, getIntercomUser } = auth
    const { isLoaded, chatStarted } = plugin

    watch(user, (user) => {
        if (!window) {
            return
        }

        if (!isLoaded.value) {
            return
        }

        if (user) {
            window.Intercom?.('update', getIntercomUser())
        } else {
            window.Intercom?.('shutdown')

            chatStarted.value = false
        }
    })

    app.provide(key, plugin)

    return plugin
}

function createIntercom(
    cookiePlugin: ReactiveCookiePlugin,
    appId: string | undefined
): IntercomPlugin {
    const isLoaded = ref(false)
    const unreadCount = ref(0)
    const chatStarted = cookiePlugin.createBooleanCookie('chat_started')
    const isAdmin = cookiePlugin.createIsAdminCookie()

    // eslint-disable-next-line vue-composable/composable-placement
    const { load, unload } = useScriptTag(
        `https://widget.intercom.io/widget/${appId}`,
        undefined,
        { manual: true, immediate: false, async: true }
    )

    const window = getWindow()

    let loadingPromise: Promise<boolean | HTMLScriptElement> | null = null

    const loadIntercom = async () => {
        if (isLoaded.value) {
            return Promise.resolve()
        }

        if (loadingPromise) {
            return loadingPromise
        }

        if (isAdmin.value) {
            return Promise.reject('[Intercom] disabled because you are admin')
        }

        if (!appId) {
            return Promise.reject('[Intercom] appId not configured')
        }

        if (!window) {
            return Promise.reject('[Intercom] window is not defined')
        }

        loadingPromise = load()

        return loadingPromise.then(() => {
            isLoaded.value = true
            loadingPromise = null
        })
    }

    return {
        appId,

        isLoaded,
        unreadCount,
        chatStarted,

        load: loadIntercom,
        unload,
    }
}

export function useIntercom(): IntercomPlugin {
    return inject(key) as IntercomPlugin
}
